import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin, Empty, Card, Tooltip } from 'antd';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { MakeApiCall } from '../../../../../apis';
import { BsrModalContent } from '../style';
import { useThemeMode } from '../../../../../contexts/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const SUB_COLOR = '#314F71';
const ROOT_COLOR = '#f45b68';

const CHART_PALETTE = {
  light: {
    tick: '#888',
    grid: '#f0f0f0',
    tooltipBg: '#ffffff',
    tooltipTitle: '#333',
    tooltipBody: '#666',
    tooltipBorder: '#ddd',
    pointBorder: '#ffffff',
    legend: '#3f4254',
  },
  dark: {
    tick: '#a1a5b7',
    grid: 'rgba(161, 165, 183, 0.32)',
    tooltipBg: '#2b2b40',
    tooltipTitle: '#e1e3ea',
    tooltipBody: '#a1a5b7',
    tooltipBorder: '#3f4254',
    pointBorder: '#1e1e2d',
    legend: '#e1e3ea',
  },
};

const formatDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const formatDateForXAxis = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });

const formatTooltipDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

const fetchAllPages = async (asin, startDate, endDate, token) => {
  const res = await MakeApiCall(
    'products/sales-rank-history?no_pagination=true',
    'post',
    { asin, start_date: startDate, end_date: endDate },
    true,
    {},
    token
  );
  if (!res?.status) throw new Error(res?.message || 'Failed to load BSR history');
  return res.data?.records || [];
};

const processRecords = (records) => {
  if (!records.length) {
    return {
      timestamps: [],
      subData: [],
      rootData: [],
      subLabel: '',
      rootLabel: '',
      rawRecords: []
    };
  }

  const sorted = [...records].sort((a, b) => a.created_at - b.created_at);
  const timestamps = sorted.map((r) => r.created_at);
  const subData = sorted.map((r) => r.subcategory_sales_rank);
  const rootData = sorted.map((r) => r.root_category_sales_rank);
  const xAxisLabels = sorted.map((r) => formatDateForXAxis(r.created_at));
  const subLabel = records[0]?.subcategory_title || 'Subcategory';
  const rootLabel = records[0]?.root_category_title || 'Root Category';

  return {
    timestamps,
    subData,
    rootData,
    xAxisLabels,
    subLabel,
    rootLabel,
    rawRecords: sorted
  };
};

const SalesRankModal = ({ open, onClose, asin, startDate, endDate, token }) => {
  const { themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';
  const palette = CHART_PALETTE[isDark ? 'dark' : 'light'];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartInfo, setChartInfo] = useState(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!open || !asin) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setChartInfo(null);
      try {
        const records = await fetchAllPages(asin, startDate, endDate, token);

        if (!records.length) {
          setChartInfo({
            timestamps: [],
            subData: [],
            rootData: [],
            xAxisLabels: [],
            subLabel: '',
            rootLabel: '',
            rawRecords: []
          });
          return;
        }

        setChartInfo(processRecords(records));
      } catch (e) {
        setError(e.message || 'Failed to load BSR history');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, asin, startDate, endDate, token]);

  const hasData = chartInfo && chartInfo.timestamps && chartInfo.timestamps.length > 0;

  const chartData = useMemo(
    () => ({
      labels: hasData ? chartInfo.xAxisLabels : [],
      datasets: [
        {
          label: chartInfo?.subLabel || 'Subcategory',
          data: hasData ? chartInfo.subData : [],
          borderColor: isDark ? '#6ea8fe' : SUB_COLOR,
          backgroundColor: 'transparent',
          borderWidth: 2.5,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: isDark ? '#6ea8fe' : SUB_COLOR,
          pointBorderColor: palette.pointBorder,
          pointBorderWidth: 0,
          yAxisID: 'y',
        },
        {
          label: chartInfo?.rootLabel || 'Root Category',
          data: hasData ? chartInfo.rootData : [],
          borderColor: ROOT_COLOR,
          backgroundColor: 'transparent',
          borderWidth: 2.5,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: ROOT_COLOR,
          pointBorderColor: palette.pointBorder,
          pointBorderWidth: 0,
          yAxisID: 'y1',
        }
      ]
    }),
    [chartInfo, hasData, isDark, palette.pointBorder]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex;
              if (chartInfo?.timestamps?.[index]) {
                return formatTooltipDateTime(chartInfo.timestamps[index]);
              }
              return tooltipItems[0].label;
            },
            label: (context) => {
              let label = context.dataset.label || '';
              if (label) label += ': ';
              const value = context.parsed.y;
              label += value == null ? '—' : `#${Math.round(value).toLocaleString()}`;
              return label;
            }
          },
          backgroundColor: palette.tooltipBg,
          titleColor: palette.tooltipTitle,
          bodyColor: palette.tooltipBody,
          borderColor: palette.tooltipBorder,
          borderWidth: 1,
          padding: 10,
          displayColors: true,
        },
        legend: {
          position: 'top',
          align: 'center',
          labels: {
            font: { size: 12 },
            color: palette.legend,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
            padding: 12,
          }
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 12 },
            color: palette.tick,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
          },
          grid: { display: false },
          border: { display: false }
        },
        y: {
          position: 'left',
          ticks: {
            callback: (value) => (value == null ? '—' : `#${Math.round(value).toLocaleString()}`),
            font: { size: 12 },
            color: isDark ? '#8eb8e8' : SUB_COLOR,
          },
          grid: {
            color: palette.grid,
            drawBorder: false,
            lineWidth: 1,
            tickWidth: 0,
          },
        },
        y1: {
          position: 'right',
          ticks: {
            callback: (value) => (value == null ? '—' : `#${Math.round(value).toLocaleString()}`),
            font: { size: 12 },
            color: ROOT_COLOR,
          },
          grid: { display: false },
        }
      },
      elements: {
        line: {
          borderJoin: 'round',
          borderCap: 'round',
        },
        point: {
          hoverRadius: 5,
          radius: 0,
        }
      },
      layout: {
        padding: { top: 10, bottom: 0, left: 5, right: 5 }
      }
    }),
    [chartInfo, palette, isDark]
  );

  const latestSub = hasData ? chartInfo.subData[chartInfo.subData.length - 1] : null;
  const latestRoot = hasData ? chartInfo.rootData[chartInfo.rootData.length - 1] : null;
  const bestSub = hasData ? Math.min(...chartInfo.subData.filter((v) => v != null)) : null;
  const bestRoot = hasData ? Math.min(...chartInfo.rootData.filter((v) => v != null)) : null;
  const worstSub = hasData ? Math.max(...chartInfo.subData.filter((v) => v != null)) : null;
  const worstRoot = hasData ? Math.max(...chartInfo.rootData.filter((v) => v != null)) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      className="item-list-bsr-modal"
      classNames={{
        header: 'item-list-bsr-modal-header',
        body: 'item-list-bsr-modal-body',
      }}
      title={
        <div className="d-flex flex-column gap-1">
          <span className="bsr-modal-title">BSR Trend Analysis</span>
          <div className="bsr-modal-meta d-flex flex-wrap gap-2 align-items-center">
            <span><b>ASIN:</b> {asin}</span>
            {startDate && endDate && (
              <span className="ms-3">
                <i className="ki-outline ki-filter-search text-danger fs-6" /> Filter: {startDate} → {endDate}
              </span>
            )}
            <Tooltip title="Change date range from the main dashboard.">
              <i className="ki-outline ki-information-2 fs-5 text-muted" style={{ cursor: 'pointer' }} />
            </Tooltip>
          </div>
        </div>
      }
      destroyOnClose
      centered
    >
      <BsrModalContent>
        {hasData && (
          <div className="d-flex gap-3 mb-3 flex-wrap">
            <Card className="bsr-stat-card bsr-stat-card--sub">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="bsr-stat-label">{chartInfo.subLabel}</div>
                  <div className="bsr-stat-value--sub">#{latestSub?.toLocaleString()}</div>
                </div>
                <div className="text-end">
                  <div className="fs-5 d-flex align-items-center gap-1">
                    <i className="ki-outline ki-cup fs-5 text-success" />
                    <span className="text-success">{bestSub?.toLocaleString()}</span>
                    <span className="text-muted mx-1">/</span>
                    <i className="ki-outline ki-graph-up fs-5 text-danger" />
                    <span className="text-danger">{worstSub?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bsr-stat-card bsr-stat-card--root">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="bsr-stat-label">{chartInfo.rootLabel}</div>
                  <div className="bsr-stat-value--root">#{latestRoot?.toLocaleString()}</div>
                </div>
                <div className="text-end">
                  <div className="d-flex align-items-center gap-1">
                    <i className="ki-outline ki-cup fs-5 text-success" />
                    <span className="text-success">{bestRoot?.toLocaleString()}</span>
                    <span className="text-muted mx-1">/</span>
                    <i className="ki-outline ki-graph-up fs-5 text-danger" />
                    <span className="text-danger">{worstRoot?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="bsr-chart-wrap">
          {loading && (
            <div className="d-flex justify-content-center align-items-center bsr-chart-wrap">
              <Spin size="large" tip="Loading BSR history..." />
            </div>
          )}

          {!loading && error && (
            <div className="d-flex justify-content-center align-items-center bsr-chart-wrap">
              <Empty description={error} />
            </div>
          )}

          {!loading && !error && chartInfo && chartInfo.timestamps.length === 0 && (
            <div className="d-flex justify-content-center align-items-center bsr-chart-wrap">
              <Empty description="No BSR data available for the selected period" />
            </div>
          )}

          {!loading && !error && hasData && (
            <Line
              key={themeMode}
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              height={100}
            />
          )}
        </div>

        <div className="separator border-gray-300 separator-dashed" />

        {hasData && (
          <div className="mt-4">
            <details>
              <summary className="bsr-details-summary">
                View detailed data with date and time
              </summary>
              <div className="bsr-table-wrap">
                <table className="table table-sm table-hover bsr-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date & Time</th>
                      <th>Subcategory Rank</th>
                      <th>Root Category Rank</th>
                      <th>Subcategory Title</th>
                      <th>Root Category Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartInfo.rawRecords.map((record, idx) => (
                      <tr key={record.id}>
                        <td>{idx + 1}</td>
                        <td>{formatDateTime(record.created_at)}</td>
                        <td>
                          <span className="bsr-rank--sub">
                            #{record.subcategory_sales_rank?.toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span className="bsr-rank--root">
                            #{record.root_category_sales_rank?.toLocaleString()}
                          </span>
                        </td>
                        <td>{record.subcategory_title}</td>
                        <td>{record.root_category_title}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
      </BsrModalContent>
    </Modal>
  );
};

export default SalesRankModal;
